import { useState } from 'react'

interface RouteInfo {
  method: string
  path: string
  description: string
  auth: 'none' | 'cognito' | 'admin'
  queryParams?: string
  body?: string
  response?: string
}

interface RouteGroup {
  name: string
  description: string
  routes: RouteInfo[]
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-800',
  POST: 'bg-blue-100 text-blue-800',
  PUT: 'bg-yellow-100 text-yellow-800',
  DELETE: 'bg-red-100 text-red-800',
}

const AUTH_LABELS: Record<string, { label: string; color: string }> = {
  none: { label: 'Public', color: 'bg-gray-100 text-gray-600' },
  cognito: { label: 'Cognito JWT', color: 'bg-purple-100 text-purple-700' },
  admin: { label: 'Admin Only', color: 'bg-orange-100 text-orange-700' },
}

const API_GROUPS: RouteGroup[] = [
  {
    name: 'Authentication',
    description: 'Login, token management, and profile. No Cognito auth required (pre-login).',
    routes: [
      { method: 'POST', path: '/auth/login', description: 'Authenticate with email and password. Returns accessToken, idToken, refreshToken.', auth: 'none', body: '{ email, password }', response: '{ accessToken, idToken, refreshToken, expiresIn, user }' },
      { method: 'POST', path: '/auth/send-credentials', description: 'Send login credentials email to a user (admin-initiated).', auth: 'none', body: '{ email }' },
      { method: 'POST', path: '/auth/change-password', description: 'Change password using current credentials.', auth: 'none', body: '{ email, previousPassword, proposedPassword }' },
      { method: 'POST', path: '/auth/refresh-token', description: 'Refresh expired tokens. Returns new accessToken and idToken.', auth: 'none', body: '{ refreshToken }', response: '{ accessToken, idToken, expiresIn }' },
      { method: 'POST', path: '/auth/logout', description: 'Invalidate refresh token and sign out.', auth: 'none', body: '{ refreshToken }' },
      { method: 'POST', path: '/auth/reset-password', description: 'Reset password using confirmation code.', auth: 'none', body: '{ email, confirmationCode, newPassword }' },
      { method: 'GET', path: '/auth/profile', description: 'Get authenticated user profile.', auth: 'cognito' },
      { method: 'PUT', path: '/auth/profile', description: 'Update authenticated user profile.', auth: 'cognito', body: '{ firstName?, lastName?, ... }' },
    ],
  },
  {
    name: 'Users',
    description: 'User data, membership cards, and verification.',
    routes: [
      { method: 'GET', path: '/users/{userId}', description: 'Get basic user profile (userId, name, email, role, participantNumber, faiNation).', auth: 'cognito', response: '{ userId, firstName, lastName, email, role, participantNumber, faiNation }' },
      { method: 'GET', path: '/users/membership-card', description: 'Generate membership card image for the authenticated user.', auth: 'cognito', response: 'PNG image (binary)' },
      { method: 'GET', path: '/users/membership-card/wallet', description: 'Generate Apple/Google wallet pass for the authenticated user.', auth: 'cognito', response: 'Wallet pass file (binary)' },
    ],
  },
  {
    name: 'Verification (Public)',
    description: 'Public pilot verification endpoint. No authentication required.',
    routes: [
      { method: 'GET', path: '/verify/{userId}', description: 'Verify a pilot by userId. Returns public pilot info for QR code scanning.', auth: 'none', response: '{ userId, firstName, lastName, participantNumber, faiNation, verified }' },
    ],
  },
  {
    name: 'Events',
    description: 'Event scheduling, signup, and withdrawal. All routes require Cognito JWT.',
    routes: [
      { method: 'GET', path: '/events', description: 'List events. Supports filtering.', auth: 'cognito', queryParams: 'upcoming=true | date=YYYY-MM-DD', response: '{ events: [...] }' },
      { method: 'POST', path: '/events', description: 'Create a new event (admin).', auth: 'cognito', body: '{ title, description, date, startTime, endTime, location, type, maxParticipants? }' },
      { method: 'GET', path: '/events/{eventId}', description: 'Get a single event by ID.', auth: 'cognito', response: '{ event: { ... } }' },
      { method: 'PUT', path: '/events/{eventId}', description: 'Update an existing event (admin).', auth: 'cognito', body: '{ title?, description?, date?, startTime?, endTime?, location?, type?, maxParticipants? }' },
      { method: 'DELETE', path: '/events/{eventId}', description: 'Delete an event (admin).', auth: 'cognito' },
      { method: 'POST', path: '/events/{eventId}/signup', description: 'Sign up the authenticated user for an event.', auth: 'cognito' },
      { method: 'DELETE', path: '/events/{eventId}/withdraw', description: 'Withdraw the authenticated user from an event.', auth: 'cognito' },
    ],
  },
  {
    name: 'Photos',
    description: 'Photo competition themes, uploads, submissions, and publishing.',
    routes: [
      { method: 'GET', path: '/photos/themes', description: "Get today's active themes (no query params) or filtered themes (with query params).", auth: 'cognito', queryParams: 'date=YYYY-MM-DD | all=true', response: '{ themes: [...] }' },
      { method: 'POST', path: '/photos/themes', description: 'Create a new photo theme (admin).', auth: 'cognito', body: '{ title, description, date, competitionId? }' },
      { method: 'PUT', path: '/photos/themes/{themeId}', description: 'Update a theme (admin).', auth: 'cognito', body: '{ title?, description?, date? }' },
      { method: 'DELETE', path: '/photos/themes/{themeId}', description: 'Delete a theme (admin).', auth: 'cognito' },
      { method: 'GET', path: '/photos/themes/{themeId}/submissions', description: 'Get all submissions for a theme (admin).', auth: 'cognito', response: '{ submissions: [...] }' },
      { method: 'POST', path: '/photos/upload', description: 'Upload a single photo submission.', auth: 'cognito', body: 'multipart/form-data: { photo, themeId }' },
      { method: 'POST', path: '/photos/upload/batch', description: 'Upload multiple photos in one request.', auth: 'cognito', body: 'multipart/form-data: { photos[], themeId }' },
      { method: 'POST', path: '/photos/submissions/{submissionId}/publish', description: 'Publish a submission to the gallery (admin).', auth: 'cognito' },
      { method: 'POST', path: '/photos/submissions/{submissionId}/unpublish', description: 'Unpublish a submission from the gallery (admin).', auth: 'cognito' },
      { method: 'POST', path: '/photos/submissions/batch-publish', description: 'Publish multiple submissions at once (admin).', auth: 'cognito', body: '{ submissionIds: [...] }' },
      { method: 'GET', path: '/photos/submissions/download', description: 'Download all submissions for a date as a ZIP file.', auth: 'cognito', queryParams: 'date=YYYY-MM-DD', response: 'ZIP file (binary)' },
      { method: 'GET', path: '/photos/gallery', description: 'Get published photos for the gallery.', auth: 'cognito', response: '{ photos: [...] }' },
    ],
  },
  {
    name: 'Admin',
    description: 'Administrative endpoints. Require Cognito JWT with admin role.',
    routes: [
      { method: 'GET', path: '/admin/users', description: 'List all users in the system.', auth: 'admin', response: '{ users: [...] }' },
      { method: 'POST', path: '/admin/users', description: 'Create a single user (creates in Cognito + DynamoDB).', auth: 'admin', body: '{ email, firstName, lastName, role?, participantNumber?, faiNation? }' },
      { method: 'DELETE', path: '/admin/users/{userId}', description: 'Delete a specific user from Cognito and DynamoDB.', auth: 'admin' },
      { method: 'DELETE', path: '/admin/users/all', description: 'Delete ALL users (development only). Use with extreme caution.', auth: 'admin' },
      { method: 'POST', path: '/admin/users/import', description: 'Import users from XLSX file. Creates Cognito accounts and DynamoDB records.', auth: 'admin', body: 'multipart/form-data: { file }' },
      { method: 'POST', path: '/admin/users/{userId}/resend-invite', description: 'Resend login credentials email to a user.', auth: 'admin' },
      { method: 'POST', path: '/admin/users/send-all-invites', description: 'Send invite emails to all users who have not been invited yet.', auth: 'admin' },
      { method: 'PUT', path: '/admin/users/{userId}/role', description: 'Update a user role (admin, photo_admin, pilot).', auth: 'admin', body: '{ role }' },
      { method: 'POST', path: '/admin/headshots/upload-url', description: 'Generate a presigned S3 URL for headshot upload.', auth: 'admin', body: '{ fileName, contentType }', response: '{ uploadUrl, key }' },
      { method: 'GET', path: '/admin/headshots', description: 'List all uploaded headshot images.', auth: 'admin', response: '{ headshots: [...] }' },
      { method: 'POST', path: '/admin/headshots/link', description: 'Link a headshot image to a user.', auth: 'admin', body: '{ userId, headshotKey }' },
      { method: 'GET', path: '/admin/competitions', description: 'List all competitions.', auth: 'admin', response: '{ competitions: [...] }' },
      { method: 'POST', path: '/admin/competitions', description: 'Create a new competition.', auth: 'admin', body: '{ name, year, startDate, endDate }' },
      { method: 'PUT', path: '/admin/competitions/{competitionId}', description: 'Update competition details.', auth: 'admin', body: '{ name?, year?, startDate?, endDate? }' },
      { method: 'POST', path: '/admin/competitions/{competitionId}/set-active', description: 'Set a competition as the active competition.', auth: 'admin' },
    ],
  },
  {
    name: 'Public Gallery',
    description: 'Public gallery endpoints for the gallery subdomain. No authentication required.',
    routes: [
      { method: 'GET', path: '/public/gallery/years', description: 'Get list of years that have published gallery photos.', auth: 'none', response: '{ years: [2025, 2026, ...] }' },
      { method: 'GET', path: '/public/gallery/years/{year}/themes', description: 'Get themes with published photos for a given year.', auth: 'none', response: '{ themes: [...] }' },
      { method: 'GET', path: '/public/gallery/themes/{themeId}/photos', description: 'Get published photos for a specific theme.', auth: 'none', response: '{ photos: [...] }' },
    ],
  },
]

function RouteCard({ route }: { route: RouteInfo }) {
  const [expanded, setExpanded] = useState(false)
  const authInfo = AUTH_LABELS[route.auth]
  const hasDetails = route.queryParams || route.body || route.response

  return (
    <div
      className={`border rounded-lg p-3 hover:shadow-sm transition-shadow ${hasDetails ? 'cursor-pointer' : ''}`}
      onClick={() => hasDetails && setExpanded(!expanded)}
    >
      <div className="flex items-start gap-3">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-semibold min-w-[52px] text-center ${METHOD_COLORS[route.method]}`}>
          {route.method}
        </span>
        <div className="flex-1 min-w-0">
          <code className="text-sm font-mono text-gray-800 break-all">{route.path}</code>
          <p className="text-sm text-gray-600 mt-1">{route.description}</p>
        </div>
        <span className={`inline-block px-2 py-0.5 rounded text-xs whitespace-nowrap ${authInfo.color}`}>
          {authInfo.label}
        </span>
        {hasDetails && (
          <span className="text-gray-400 text-xs mt-1">{expanded ? '▲' : '▼'}</span>
        )}
      </div>
      {expanded && (
        <div className="mt-3 ml-[68px] space-y-2 text-sm">
          {route.queryParams && (
            <div>
              <span className="font-medium text-gray-700">Query params: </span>
              <code className="text-gray-600 bg-gray-50 px-1 rounded">{route.queryParams}</code>
            </div>
          )}
          {route.body && (
            <div>
              <span className="font-medium text-gray-700">Request body: </span>
              <code className="text-gray-600 bg-gray-50 px-1 rounded">{route.body}</code>
            </div>
          )}
          {route.response && (
            <div>
              <span className="font-medium text-gray-700">Response: </span>
              <code className="text-gray-600 bg-gray-50 px-1 rounded">{route.response}</code>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ApiReference() {
  const [search, setSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(API_GROUPS.map(g => g.name)))

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(name)) { next.delete(name) } else { next.add(name) }
      return next
    })
  }

  const filteredGroups = API_GROUPS.map(group => ({
    ...group,
    routes: group.routes.filter(r =>
      !search ||
      r.path.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase()) ||
      r.method.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(g => g.routes.length > 0)

  const totalRoutes = API_GROUPS.reduce((sum, g) => sum + g.routes.length, 0)

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">API Reference</h3>
            <p className="text-sm text-gray-500 mt-1">
              {totalRoutes} endpoints across {API_GROUPS.length} groups &middot; Base URL: <code className="bg-gray-100 px-1 rounded">/v1</code>
            </p>
          </div>
          <input
            type="text"
            placeholder="Search routes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4 flex flex-wrap gap-4 text-xs">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-300" /> GET</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-100 border border-blue-300" /> POST</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-yellow-100 border border-yellow-300" /> PUT</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" /> DELETE</span>
          <span className="mx-2 text-gray-300">|</span>
          <span className={`px-2 py-0.5 rounded ${AUTH_LABELS.none.color}`}>Public</span>
          <span className={`px-2 py-0.5 rounded ${AUTH_LABELS.cognito.color}`}>Cognito JWT</span>
          <span className={`px-2 py-0.5 rounded ${AUTH_LABELS.admin.color}`}>Admin Only</span>
        </div>

        <p className="text-xs text-gray-500 mb-6">
          All authenticated endpoints require an <code className="bg-gray-100 px-1 rounded">Authorization: Bearer &lt;ID_TOKEN&gt;</code> header.
          Use the ID token (not access token) from the login response. Click a route to see details.
        </p>

        <div className="space-y-4">
          {filteredGroups.map(group => (
            <div key={group.name} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleGroup(group.name)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div>
                  <span className="font-semibold text-gray-900">{group.name}</span>
                  <span className="ml-2 text-xs text-gray-500">({group.routes.length} routes)</span>
                  <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>
                </div>
                <span className="text-gray-400">{expandedGroups.has(group.name) ? '▲' : '▼'}</span>
              </button>
              {expandedGroups.has(group.name) && (
                <div className="p-3 space-y-2">
                  {group.routes.map((route, i) => (
                    <RouteCard key={`${route.method}-${route.path}-${i}`} route={route} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <p className="text-center text-gray-500 py-8">No routes match "{search}"</p>
          )}
        </div>
      </div>
    </div>
  )
}
